import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Dropdown,
  Card,
  Badge,
  Col,
} from "react-bootstrap";
import {
  Formik,
  Field,
  Form as FormikForm,
  ErrorMessage,
  FieldArray,
} from "formik";
import * as Yup from "yup";
import { Check, MessageCircle, Pencil, Trash2 } from "lucide-react";
import PageTitle from "../layouts/PageTitle";
import ConfirmDeleteModal from "../Modals/ConfirmDelete";
import {
  changeRequestUserPlan,
  deletePlan,
  deleteRequestUserPlan,

  getNEWUSERS,
  getSUBSCRIPTION,
  postPlan,

  putPlan,

} from "../../services/UserServices/UserService";
import toast from "react-hot-toast";
import Select from "react-select";
import PageContentLoader from "../components/TableLoader";
import { Tabs, Tab, Table } from "react-bootstrap";
import Pagination from "../components/Pagination";
import { debounce, set } from "lodash";
import { useNavigate } from "react-router-dom";

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return value ?? "—";
  return num.toLocaleString("en-IN");
};

const SubscriptionPlanCard = ({ plan, onEdit, onDelete }) => {
  return (
    <div className="col-lg-4 col-md-6 d-flex">
      <article className="qx-subscription-card flex-fill">
        <div className="qx-subscription-card__header">
          <div className="qx-subscription-card__title-wrap">
            <p className="qx-subscription-card__label">Plan</p>
            <h3 className="qx-subscription-card__name">{plan.name}</h3>
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto">
            {plan?.isPartnerSubscription === true && (
              <button
                type="button"
                className="qx-subscription-card__edit"
                onClick={() => onEdit(plan)}
                aria-label={`Edit ${plan.name} plan`}
              >
                <Pencil size={15} />
                Edit
              </button>
            )}
            {plan?.isPartnerSubscription === true && (
              <button
                type="button"
                className="qx-subscription-card__edit text-danger"
                style={{ borderColor: "rgba(220, 38, 38, 0.3)", color: "#dc2626" }}
                onClick={() => onDelete(plan)}
                aria-label={`Delete ${plan.name} plan`}
              >
                <Trash2 size={15} />
                Delete
              </button>
            )}
          </div>

        </div>

        <div className="qx-subscription-card__pricing">
          <div className="qx-subscription-card__price">
            <span className="qx-subscription-card__currency">₹</span>
            {formatCurrency(plan.price)}
          </div>
          {plan.strikeOut ? (
            <div className="qx-subscription-card__strike">
              ₹ {formatCurrency(plan.strikeOut)}
            </div>
          ) : (
            <div className="qx-subscription-card__strike qx-subscription-card__strike--empty" aria-hidden="true">
              &nbsp;
            </div>
          )}
        </div>

        <div className="qx-subscription-card__meta">
          <div className="qx-subscription-card__meta-item">
            <span className="qx-subscription-card__meta-icon">
              <MessageCircle size={16} />
            </span>
            <div>
              <span className="qx-subscription-card__meta-value">
                {plan.whatsappMessageLimit ?? "—"}
              </span>
              <span className="qx-subscription-card__meta-label">WhatsApp messages</span>
            </div>
          </div>
        </div>

        <div className="qx-subscription-card__features">
          <p className="qx-subscription-card__features-title">Features included</p>
          <ul className="qx-subscription-feature-list">
            {plan?.features?.length ? (
              plan.features.map((feature, featureIndex) => (
                <li key={`${feature}-${featureIndex}`} className="qx-subscription-feature-item">
                  <span className="qx-subscription-feature-check">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span>{feature}</span>
                </li>
              ))
            ) : (
              <li className="qx-subscription-feature-item qx-subscription-feature-item--empty">
                No features listed
              </li>
            )}
          </ul>
        </div>
      </article>
    </div>
  );
};

const Subscriptions = () => {
  // ---- Static Subscription Data ----
  const [subscriptions, setSubscriptions] = useState([]);
  const [loader, setLoader] = useState(false);
  const [activeTab, setActiveTab] = useState("plans");
  const [planRequests, setPlanRequests] = useState([]);
  const [requestLoader, setRequestLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [plans, setPlans] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();
  const fetchSubscription = async () => {
    try {
      setLoader(true);
      const res = await getSUBSCRIPTION();
      setSubscriptions(res?.data?.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchSubscription();
  }, []);

  // Example API call
  const fetchPlanRequests = async () => {
    try {
      setRequestLoader(true);
      const res = await getNEWUSERS(
        currentPage,
        itemsPerPage,
        debouncedSearch,
        "updatePlan",
        filterDate,
        endDate,
      );
      console.log("Plan Requests:", res?.data?.data?.users);
      setPlanRequests(res?.data?.data?.users);
      setTotalItems(res?.data?.data?.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setRequestLoader(false);
    }
  };

  useEffect(() => {
    if (activeTab === "requests") {
      setCurrentPage(0); // Reset to first page on tab change
      setSearch(""); // Clear search on tab change
      fetchPlanRequests();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "requests") {
      fetchPlanRequests();
    }
  }, [currentPage, debouncedSearch, filterDate, endDate]);

  const debouncedHandler = useMemo(
    () =>
      debounce((value) => {
        // 🔥 hit API only if 3+ chars OR empty (for reset)
        if (value.length >= 1 || value.length === 0) {
          setDebouncedSearch(value);
          setCurrentPage(0); // reset pagination
        }
      }, 800),
    [],
  );

  useEffect(() => {
    if (activeTab === "requests") {
      setCurrentPage(0); // Reset to first page on search change
      fetchPlanRequests();
    }
  }, [debouncedSearch, filterDate, endDate]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedItemToReject, setSelectedItemToReject] = useState(null);
  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setSelectedItemToReject(null);
  };

  const handleOpenRejectModal = (item) => {
    setSelectedItemToReject(item);
    setShowRejectModal(true);
  };
  const handleConfirmReject = () => {
    if (selectedItemToReject) {
      deleteRequestUserPlan(selectedItemToReject._id)
        .then(() => {
          toast.success("Request rejected");
          fetchPlanRequests();
          handleCloseRejectModal(); // Close the modal on success
        })
        .catch((error) => {
          // Optional: Handle error
          toast.error("Failed to reject plan");
          handleCloseRejectModal();
        });
    }
  };
  const renderPlanRequestTable = (data) => (
    <>
      <Card>
        <Card.Body>
          <Table responsive>
            <thead style={{ color: "#A60000" }}>
              <tr>
                <th>
                  <strong>S.NO</strong>
                </th>
                <th>
                  <strong>Name</strong>
                </th>

                <th>
                  <strong>Phone</strong>
                </th>
                <th>
                  <strong>Current Plan</strong>
                </th>

                <th>
                  <strong>Request Date</strong>
                </th>

                <th>
                  <strong>Action</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => (
                <tr key={item?._id} style={{ color: "#A60000" }}>
                  <td>{index + 1}</td>
                  <td
                    className="text-blue cursor-pointer"
                    onClick={() => navigate(`/user-details/${item?._id}`)}
                  >
                    {item?.hostName}
                  </td>

                  <td>
                    {item?.countryCode} {item?.phoneNumber}
                  </td>
                  <td>
                    <Badge bg="" className="badge-success">
                      {item?.plan?.name}
                    </Badge>
                  </td>

                  <td>
                    {item.upgradePlanRequestDate
                      ? new Date(
                        item.upgradePlanRequestDate,
                      ).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      : "_"}
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="info light"
                        className="qx-action-toggle"
                      >
                        {svg1}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleModalShow(item)}>
                          Change Plan
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleOpenRejectModal(item)}
                        >
                          Reject
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Modal
        show={showRejectModal}
        onHide={handleCloseRejectModal}
        centered
        className="qx-modal"
        dialogClassName="qx-modal-dialog"
        container={typeof document !== "undefined" ? document.body : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Rejection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reject the plan upgrade request for{" "}
          <strong>{selectedItemToReject?.hostName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseRejectModal}>
            Cancel
          </Button>
          <Button className="btn-primary qx-btn-brand" onClick={handleConfirmReject}>
            Yes, Reject Plan
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );
  // ---- Modal States ----
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenDeleteModal = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    try {
      setDeleteLoading(true);
      const planId = planToDelete._id || planToDelete.id;
      await deletePlan(planId);
      toast.success("Subscription plan deleted successfully");
      setShowDeleteModal(false);
      setPlanToDelete(null);
      fetchSubscription();
    } catch (error) {
      console.error("Error deleting subscription plan:", error);
      toast.error(error?.response?.data?.message || "Failed to delete subscription plan");
    } finally {
      setDeleteLoading(false);
    }
  };
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reqModalShow, setReqModalShow] = useState(false);
  // ---- Yup Validation Schema ----
  const positiveNumberString = Yup.string()
    .required("Required")
    .matches(/^\d+(\.\d+)?$/, "Only numbers and one dot allowed")
    .test(
      "not-negative",
      "Value cannot be negative",
      (value) => value !== undefined && value !== "" && Number(value) >= 0,
    );

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Plan name is required"),
    whatsappMessageLimit: Yup.number()
      .typeError("WhatsApp message limit must be a number")
      .required("WhatsApp message limit is required")
      .min(0, "WhatsApp message limit cannot be negative"),
    price: positiveNumberString.test(
      "positive-price",
      "Price must be greater than 0",
      (value) => value === undefined || value === "" || Number(value) > 0,
    ),
    strikeOut: positiveNumberString.test(
      "strikeout-greater-than-price",
      "Strike-out price must be greater than plan price",
      function (value) {
        const { price } = this.parent;
        if (!price || !value || Number.isNaN(Number(price)) || Number.isNaN(Number(value))) return true;
        return Number(value) > Number(price);
      },
    ),
    features: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one feature")
      .required("Features are required"),
  });
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // ---- Open Modal for Add/Edit ----
  const handleShowModal = (sub = null) => {
    setEditMode(!!sub);
    setSelectedSub(sub);
    setShowModal(true);
  };

  // ---- Handle Add/Edit Submit ----
  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (editMode) {
        const payload = {
          subscriptionId: selectedSub?._id,
          name: values.name,
          price: String(values.price),
          strikeOut: String(values.strikeOut),
          features: values.features,
          whatsappMessageLimit: values.whatsappMessageLimit
        };
        await putPlan(payload);
        toast.success("Subscription updated successfully");
      } else {
        await postPlan({
          ...values,
          price: String(values.price),
          strikeOut: String(values.strikeOut),
        });
        toast.success("Subscription added successfully");
      }
      resetForm();
      setShowModal(false);
      fetchSubscription();
    } catch (err) {
      toast.error(err?.response?.data?.message);
    }
  };

  const handleModalShow = (item) => {
    setSelectedRequest(item);
    setReqModalShow(true);
  };

  if (loader) {
    return (
      <div className="qx-page">
        <PageContentLoader message="Loading subscriptions..." />
      </div>
    );
  }

  return (
    <div className="qx-page qx-content-page qx-subscription-page">
      <PageTitle
        pageTitle="Subscription Plan"
        subtitle="Manage subscription plans and pricing"
        actions={
          <Button
            className="maroon-btn"
            onClick={() => handleShowModal(null)}
          >
            + Add Subscription
          </Button>
        }
      />

      {subscriptions?.length > 0 ? (
        <div className="row g-4 align-items-stretch qx-subscription-grid">
          {subscriptions.map((plan, index) => (
            <SubscriptionPlanCard
              key={plan._id || plan.id || index}
              plan={plan}
              onEdit={handleShowModal}
              onDelete={handleOpenDeleteModal}
            />
          ))}
        </div>
      ) : (
        <p className="qx-empty-state">No subscription plans found</p>
      )}

      {/* ---- Add/Edit Modal ---- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        size="xl"
        className="qx-modal"
        dialogClassName="qx-modal-dialog qx-modal-dialog--wide"
        container={typeof document !== "undefined" ? document.body : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "Edit Subscription" : "Add Subscription"}
          </Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            name: selectedSub?.name || "",
            price: selectedSub?.price || "",
            features: selectedSub?.features || [],
            strikeOut: selectedSub?.strikeOut || "",
            whatsappMessageLimit: selectedSub?.whatsappMessageLimit
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values }) => (
            <FormikForm>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Plan Name <span className="req">*</span>
                  </Form.Label>
                  <Field
                    type="text"
                    name="name"
                    className="form-control"
                    placeholder="Enter plan name"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-danger small"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Plan Price <span className="req">*</span>
                  </Form.Label>
                  <Field name="price">
                    {({ field, form }) => (
                      <div className="qx-modal-currency">

                        <input
                          {...field}
                          type="text"
                          className="form-control"
                          placeholder="Enter price"
                          inputMode="decimal"
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, "");
                            const parts = value.split(".");
                            if (parts.length > 2) {
                              value = parts[0] + "." + parts.slice(1).join("");
                            }
                            form.setFieldValue("price", value);
                          }}
                        />
                      </div>
                    )}
                  </Field>

                  <ErrorMessage
                    name="price"
                    component="div"
                    className="text-danger small"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Plan Strike Out <span className="req">*</span>
                  </Form.Label>
                  <Field name="strikeOut">
                    {({ field, form }) => (
                      <div className="qx-modal-currency">

                        <input
                          {...field}
                          type="text"
                          className="form-control"
                          placeholder="Enter strike price"
                          inputMode="decimal"
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, "");
                            const parts = value.split(".");
                            if (parts.length > 2) {
                              value = parts[0] + "." + parts.slice(1).join("");
                            }
                            form.setFieldValue("strikeOut", value);
                          }}
                        />
                      </div>
                    )}
                  </Field>

                  <ErrorMessage
                    name="strikeOut"
                    component="div"
                    className="text-danger small"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    WhatsApp Message Limit <span className="req">*</span>
                  </Form.Label>
                  <Field name="whatsappMessageLimit">
                    {({ field, form }) => (
                      <input
                        {...field}
                        type="text"
                        className="form-control"
                        placeholder="Enter message limit"
                        inputMode="numeric"
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9]/g, "");
                          form.setFieldValue("whatsappMessageLimit", value);
                        }}
                      />
                    )}
                  </Field>

                  <ErrorMessage
                    name="whatsappMessageLimit"
                    component="div"
                    className="text-danger small"
                  />
                </Form.Group>

                {/* <Form.Group className="mb-3">
                <Form.Label style={{ color: "#3F3F46" }}>Features</Form.Label>
                <FieldArray name="features">
                  {({ push, remove }) => (
                    <div>
                      {values.features.map((feature, index) => (
                        <div key={index} className="d-flex mb-2 align-items-center">
                          <Field
                            name={`features.${index}`}
                            placeholder={`Feature ${index + 1}`}
                            className="form-control me-2"
                            style={{ borderColor: "#A60000" }}
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            type="button"
                            onClick={() => remove(index)}
                            disabled={values.features.length === 1}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline-success"
                        size="sm"
                        type="button"
                        onClick={() => push("")}
                      >
                        + Add Feature
                      </Button>
                    </div>
                  )}
                </FieldArray>
                <ErrorMessage name="features" component="div" className="text-danger small" />
              </Form.Group> */}

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: "#3F3F46" }}>
                    Features <span className="req">*</span>
                  </Form.Label>

                  <FieldArray name="features">
                    {({ form }) => {
                      const { values, setFieldValue } = form;

                      const featureOptions = [
                        {
                          label: "RSVP Tracking & Headcount",
                          value: "RSVP Tracking & Headcount",
                        },
                        {
                          label: "Event Based Task Tracking",
                          value: "Event Based Task Tracking",
                        },
                        { label: "Announcements", value: "Announcements" },
                        {
                          label: "Multi-Event Management",
                          value: "Multi-Event Management",
                        },
                        { label: "Guest Analytics", value: "Guest Analytics" },
                        {
                          label: "Basic Invite Creation",
                          value: "Basic Invite Creation",
                        },
                        {
                          label: "WhatsApp RSVP/Reminders",
                          value: "WhatsApp RSVP/Reminders",
                        },
                        // {
                        //   label: "AI Guest Headcount Prediction",
                        //   value: "AI Guest Headcount Prediction",
                        // },
                        {
                          label: "Multi-Lingual Invites(Text/Audio)",
                          value: "Multi-Lingual Invites(Text/Audio)",
                        },
                        { label: "Baarat Tracking", value: "Baarat Tracking" },
                        {
                          label: "Photo Sharing with Guests",
                          value: "Photo Sharing with Guests",
                        },
                        { label: "Custom Branding", value: "Custom Branding" },
                        {
                          label: "Dedicated Support Manager",
                          value: "Dedicated Support Manager",
                        },
                        {
                          label: "RSVP Chase Squad",
                          value: "RSVP Chase Squad",
                        },
                        {
                          label: "Selective Photo Identification",
                          value: "Selective Photo Identification",
                        },
                        {
                          label: "Photo Sharing by Guests",
                          value: "Photo Sharing by Guests",
                        },
                      ];

                      return (
                        <div>
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            name="features"
                            options={featureOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={featureOptions.filter((opt) =>
                              values.features.includes(opt.label),
                            )}
                            onChange={(selected) => {

                              const selectedLabels = selected
                                ? selected.map((item) => item.label)
                                : [];

                              setFieldValue("features", selectedLabels);
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: "#A60000",
                              }),
                            }}
                          />

                          <ErrorMessage
                            name="features"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      );
                    }}
                  </FieldArray>
                </Form.Group>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button className="btn-primary qx-btn-brand" type="submit" disabled={isSubmitting}>
                  {editMode ? "Update Plan" : "Add Plan"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <Modal
        show={reqModalShow}
        onHide={() => setReqModalShow(false)}
        centered
        className="qx-modal"
        dialogClassName="qx-modal-dialog"
        container={typeof document !== "undefined" ? document.body : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Plan</Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            planId: selectedRequest?.plan?.id || "",
            userId: selectedRequest?._id,
          }}
          onSubmit={async (values) => {
            try {
              const payload = {
                userId: values.userId,
                subscriptionId: values.planId,
              };
              await changeRequestUserPlan(payload);
              toast.success("Plan updated");
              fetchPlanRequests();
              setReqModalShow(false);
            } catch (error) {
              console.error(error);
            }
          }}
        >
          {({ handleSubmit, values, handleChange, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Select New Plan</Form.Label>

                  <Form.Select
                    name="planId"
                    value={values.planId}
                    onChange={handleChange}
                    isInvalid={touched.planId && !!errors.planId}
                  >
                    {subscriptions?.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan?.name}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {errors.planId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setReqModalShow(false)}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  className="btn-primary qx-btn-brand"
                >
                  Save Changes
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Subscription Plan"
        message={`Are you sure you want to delete "${planToDelete?.name}" subscription plan? This action cannot be undone.`}
      />
    </div>
  );
};

export default Subscriptions;
